/* DRINK MILK: shared behaviour on every page — ticker, menu, search with live results, and the side cart.
   The side cart runs on Shopify's own cart (/cart.js), so the checkout, discounts and stock work as usual. */
(function () {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const gsap = window.gsap;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  // Prices are whole shekels; agorot only show when there are any
  const sized = (src, w) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '');
  const money = (cents) => `${(cents / 100).toLocaleString('he-IL', { maximumFractionDigits: cents % 100 ? 2 : 0 })} ₪`;

  let lenis = null;
  const lockScroll = (on) => {
    document.documentElement.classList.toggle('is-locked', on);
    if (lenis) on ? lenis.stop() : lenis.start();
  };

  /* ---------- Ticker ---------- */

  const track = $('.dm-ticker__track');
  if (track && gsap && !reducedMotion()) {
    track.append(...[...track.children].map((n) => {
      const c = n.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      $$('a', c).forEach((a) => a.setAttribute('tabindex', '-1'));
      return c;
    }));
    gsap.to(track, { xPercent: 50, duration: 40, ease: 'none', repeat: -1 });
  }

  /* ---------- Menu ---------- */

  const menu = $('#dm-menu');
  const menuToggle = $('.dm-menu-toggle');

  function openMenu() {
    closeSearch();
    menu.hidden = false;
    menuToggle.setAttribute('aria-expanded', 'true');
    lockScroll(true);
    $('.dm-menu-close').focus();
    if (gsap && !reducedMotion()) {
      gsap.timeline()
        .fromTo(menu, { clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)', duration: 0.6, ease: 'power4.inOut' })
        .from('.dm-menu__list > li', { y: 40, opacity: 0, duration: 0.6, stagger: 0.05, ease: 'power3.out' }, 0.25);
    }
  }

  function closeMenu() {
    if (!menu || menu.hidden) return;
    const done = () => {
      menu.hidden = true;
      lockScroll(false);
      menuToggle.setAttribute('aria-expanded', 'false');
      menuToggle.focus();
    };
    if (!gsap || reducedMotion()) return done();
    gsap.to(menu, { clipPath: 'inset(0 0 100% 0)', duration: 0.45, ease: 'power3.in', onComplete: done });
  }

  if (menu && menuToggle) {
    menuToggle.addEventListener('click', openMenu);
    $('.dm-menu-close').addEventListener('click', closeMenu);
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) closeMenu(); });
  }

  /* ---------- Search, with results while typing ---------- */

  // Shop titles are English, so Hebrew words are mapped to the words used in the titles
  const HEBREW = [
    [['חולצ', 'טישירט'], ['TEE', 'SHIRT']],
    [['מכנס', 'פנטס'], ['PANTS', 'SHORTS']],
    [['שורט', 'קצר'], ['SHORTS']],
    [['סט', 'סטים', 'סטי'], ['SET']],
    [['קפוצ', 'סווטשירט', 'הודי'], ['HOODIE']],
    [['אפור'], ['GREY']],
    [['שחור'], ['BLACK']],
    [['לבן', 'לבנ'], ['WHITE']],
    [['כחול'], ['BLUE']],
    [['ירוק'], ['GREEN', 'PISTACHIO']],
    [['צהוב'], ['YELLOW', 'MUSTARD']],
    [['אדום', 'אדומ'], ['RED']],
    [['ורוד'], ['PINK', 'STRAWBERRY']],
    [['בז'], ['BEIGE']],
    [['קרם'], ['CREAM']],
    [['כתום', 'כתומ'], ['ORANGE']],
    [['חרדל'], ['MUSTARD']],
    [['פיסטוק'], ['PISTACHIO']],
    [['פס', 'פסים', 'פסי'], ['STRIPE']],
    [['משבצ'], ['CHECK']],
    [['כוכב'], ['STAR']],
    [['הסוואה', 'קאמו'], ['CAMO']],
    [['קרגו', 'כיס'], ['CARGO', 'POCKET']],
    [['גינס', 'ג׳ינס', "ג'ינס", 'דנים'], ['DENIM', 'WASH']],
    [['פשתן'], ['LINEN']],
    [['וופל'], ['WAFFLE']],
    [['תות'], ['STRAWBERRY', 'BERRY']],
    [['פסטה'], ['PASTA']],
    [['פיצה'], ['SLICE']],
    [['סרדינ', 'דג'], ['SARDINES']],
    [['מלאך'], ['ANGEL']],
    [['חווה', 'חיות'], ['FARM']],
    [['ים', 'חוף'], ['SEASIDE', 'VACATION']],
    [['רוכסן'], ['ZIP']],
    [['אותיות'], ['LETTERS']],
    [['בלון'], ['BALLOON']],
    [['שוק'], ['MARKET']],
  ];
  // Short keys must match the whole word, so "סט" doesn't catch "סטרייפ" and "ים" doesn't catch "ימים"
  const englishFor = (word) => {
    for (const [keys, english] of HEBREW) {
      if (keys.some((k) => (k.length < 3 ? word === k : word.startsWith(k)))) return english;
    }
    return null;
  };
  const SIZES = ['1T', '2T', '3T', '4T', '5T'];
  const plain = (s) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');

  function matches(word, p) {
    const up = word.toUpperCase();
    const title = p.title.toUpperCase().split(' ');
    if (SIZES.includes(up)) return p.sizes.some((s) => s.size === up && s.available);
    // Drops: "N03", "n-03", "N✦03" or just "03"
    if (/^N?\W*\d{1,2}$/i.test(word) && p.drop && plain(p.drop).endsWith(plain(word).replace(/^N/, '').padStart(2, '0'))) return true;
    const english = /[א-ת]/.test(word) ? englishFor(word.replace(/^[הו](?=..)/, '')) || englishFor(word) : [up];
    if (!english) return false;
    return english.some((e) => title.some((t) => t.startsWith(e)));
  }

  // The whole catalogue is small, so it's loaded once and searched in the browser
  let catalogue = null;
  function loadCatalogue() {
    if (catalogue) return catalogue;
    catalogue = fetch(`${root}products.json?limit=250`)
      .then((r) => r.json())
      .then((data) =>
        data.products
          .filter((p) => !/gift-card/.test(p.handle))
          .map((p) => {
            const dropTag = p.tags.find((t) => /^n\d{2}$/i.test(t));
            return {
              handle: p.handle,
              title: p.title,
              price: Math.min(...p.variants.map((v) => Math.round(parseFloat(v.price) * 100))),
              drop: dropTag ? `N✦${dropTag.slice(1)}` : '',
              available: p.variants.some((v) => v.available),
              sizes: p.variants.map((v) => ({ size: (v.option1 || '').toUpperCase(), available: v.available })),
              image: p.images[0] ? p.images[0].src : '',
            };
          }),
      )
      .catch(() => {
        catalogue = null;
        return [];
      });
    return catalogue;
  }

  async function searchProducts(query) {
    const words = query.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    const found = (await loadCatalogue()).filter((p) => words.every((w) => matches(w, p)));
    return [...found.filter((p) => p.available), ...found.filter((p) => !p.available)];
  }

  const SUGGESTIONS = ['חולצות', 'מכנסיים', 'סטים', 'פסים', 'כוכבים'];
  const searchUrl = (q) => `${root}search?type=product&q=${encodeURIComponent(q)}`;
  // Every word of the query has to match the product (title words, size in stock, or drop)
  const matchesQuery = (query, p) => {
    const words = query.trim().split(/\s+/).filter(Boolean);
    return words.length > 0 && words.every((w) => matches(w, p));
  };
  window.DMSearch = { searchProducts, searchUrl, SUGGESTIONS, loadCatalogue, matchesQuery };

  const search = $('#dm-search');
  const searchToggle = $('.dm-search-toggle');

  function closeSearch() {
    if (!search || search.hidden) return;
    search.hidden = true;
    searchToggle.setAttribute('aria-expanded', 'false');
  }

  if (search && searchToggle) {
    const live = document.createElement('div');
    live.className = 'dm-search-live';
    live.setAttribute('aria-live', 'polite');
    search.append(live);
    const chips = (label) =>
      `<p class="dm-search-live__label">${label}</p><div class="dm-search-live__chips">${SUGGESTIONS.map((w) => `<a class="dm-search-chip" href="${searchUrl(w)}">${w}</a>`).join('')}</div>`;

    let asked = 0;
    async function showLive() {
      const q = search.q.value.trim();
      if (!q) {
        live.innerHTML = chips('חיפושים פופולריים');
        return;
      }
      const ticket = ++asked;
      const found = await searchProducts(q);
      if (ticket !== asked) return;
      if (!found.length) {
        live.innerHTML = `<p class="dm-search-live__none">לא מצאנו את <bdi>${escapeHtml(q)}</bdi></p>${chips('אולי תנסו:')}`;
        return;
      }
      live.innerHTML = `<ul class="dm-search-live__list" role="list">${found
        .slice(0, 4)
        .map((p) => `<li><a class="dm-search-live__item" href="${root}products/${p.handle}"><img src="${sized(p.image, 240)}" alt="" loading="lazy"><span class="dm-search-live__title">${escapeHtml(p.title)}</span><span class="dm-search-live__price dm-mono dm-ltr">${money(p.price)}</span></a></li>`)
        .join('')}</ul><a class="dm-search-live__all" href="${searchUrl(q)}">לכל ${found.length} התוצאות ←</a>`;
      if (gsap && !reducedMotion()) gsap.from('.dm-search-live__item', { y: 14, opacity: 0, duration: 0.35, stagger: 0.04, ease: 'power3.out' });
    }

    function openSearch() {
      search.hidden = false;
      searchToggle.setAttribute('aria-expanded', 'true');
      search.q.focus();
      loadCatalogue();
      showLive();
      if (gsap && !reducedMotion()) gsap.from(search, { y: -12, opacity: 0, duration: 0.35, ease: 'power3.out' });
    }

    let timer = 0;
    search.q.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(showLive, 120);
    });
    searchToggle.addEventListener('click', () => (search.hidden ? openSearch() : closeSearch()));
    $('.dm-search-close').addEventListener('click', () => { closeSearch(); searchToggle.focus(); });
    search.addEventListener('submit', (e) => { if (!search.q.value.trim()) e.preventDefault(); });
  }

  /* ---------- Side cart, on Shopify's cart ---------- */

  const cartRoot = $('.dm-cart');
  const cartToggle = $('.dm-cart-toggle');
  let cartState = null;

  const post = (url, body) =>
    fetch(`${root}${url}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.description || data.message || 'שגיאה');
        return data;
      });

  function setBadges(n) {
    $$('.dm-cart-badge').forEach((b) => {
      b.textContent = n;
      b.hidden = n === 0;
    });
  }

  function showError(text) {
    if (!cartRoot) return;
    const el = $('.dm-cart__error', cartRoot);
    el.textContent = text || '';
    el.hidden = !text;
  }

  function render() {
    if (!cartRoot || !cartState) return;
    const n = cartState.item_count;
    setBadges(n);
    $('.dm-cart__count', cartRoot).textContent = n ? `(${n})` : '';
    $('.dm-cart__empty', cartRoot).hidden = n > 0;
    $('.dm-cart__foot', cartRoot).hidden = n === 0;
    $('.dm-cart__sum', cartRoot).textContent = money(cartState.total_price);
    $('.dm-cart__list', cartRoot).innerHTML = cartState.items
      .map((i) => {
        const extra = Object.entries(i.properties || {})
          .filter(([k, v]) => v && !k.startsWith('_'))
          .map(([k, v]) => `<span class="dm-cart-line__size">${escapeHtml(k)}: ${escapeHtml(v)}</span>`)
          .join('');
        // A gift card's variant is its amount, already shown as the price
        const size = !i.gift_card && i.variant_title && i.variant_title !== 'Default Title' ? `<span class="dm-cart-line__size">מידה <bdi>${escapeHtml(i.variant_title)}</bdi></span>` : '';
        return `
        <li class="dm-cart-line" data-key="${i.key}">
          <img class="dm-cart-line__img" src="${sized(i.image, 200)}" alt="" loading="lazy">
          <div class="dm-cart-line__info">
            <a class="dm-cart-line__title" href="${i.url}">${escapeHtml(i.product_title)}</a>
            ${size}${extra}
            <div class="dm-cart-line__qty">
              <button type="button" data-step="1" aria-label="עוד אחד">+</button>
              <output class="dm-mono" aria-label="כמות">${i.quantity}</output>
              <button type="button" data-step="-1" aria-label="אחד פחות">−</button>
            </div>
          </div>
          <div class="dm-cart-line__end">
            <span class="dm-cart-line__price dm-mono dm-ltr">${money(i.final_line_price)}</span>
            <button type="button" class="dm-cart-line__remove">הסרה</button>
          </div>
        </li>`;
      })
      .join('');
  }

  function refresh() {
    return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((c) => {
        cartState = c;
        render();
        return c;
      });
  }

  function openCart() {
    if (!cartRoot || !cartRoot.hidden) return;
    cartRoot.hidden = false;
    lockScroll(true);
    $('.dm-cart__close', cartRoot).focus({ focusVisible: false });
    if (!gsap || reducedMotion()) return;
    gsap.timeline()
      .fromTo('.dm-cart__scrim', { opacity: 0 }, { opacity: 1, duration: 0.35 }, 0)
      .fromTo('.dm-cart__panel', { xPercent: -100 }, { xPercent: 0, duration: 0.55, ease: 'power4.out' }, 0)
      .from('.dm-cart-line', { x: -30, opacity: 0, duration: 0.45, stagger: 0.05, ease: 'power3.out' }, 0.15);
  }

  function closeCart() {
    if (!cartRoot || cartRoot.hidden) return;
    const done = () => {
      cartRoot.hidden = true;
      lockScroll(false);
      showError('');
      if (cartToggle) cartToggle.focus({ focusVisible: false });
    };
    if (!gsap || reducedMotion()) return done();
    gsap.to('.dm-cart__scrim', { opacity: 0, duration: 0.3 });
    gsap.to('.dm-cart__panel', { xPercent: -100, duration: 0.4, ease: 'power3.in', onComplete: done });
  }

  // The picture of the piece flies into the cart icon
  function fly(from) {
    if (!from || !cartToggle || !gsap || reducedMotion()) return;
    const a = from.getBoundingClientRect();
    const b = cartToggle.getBoundingClientRect();
    const ghost = from.cloneNode(from.tagName !== 'IMG');
    ghost.className = 'dm-cart-ghost';
    Object.assign(ghost.style, { left: `${a.left}px`, top: `${a.top}px`, width: `${a.width}px`, height: `${a.height}px`, margin: 0 });
    document.body.append(ghost);
    gsap.to(ghost, {
      x: b.left + b.width / 2 - (a.left + a.width / 2),
      y: b.top + b.height / 2 - (a.top + a.height / 2),
      scale: 0.08,
      opacity: 0.4,
      duration: 0.7,
      ease: 'power3.in',
      onComplete: () => {
        ghost.remove();
        gsap.fromTo(cartToggle, { scale: 1.25 }, { scale: 1, duration: 0.4, ease: 'back.out(3)' });
      },
    });
  }

  // Adds a size (variant) to Shopify's cart, then opens the side cart
  async function add(variantId, { quantity = 1, properties, from } = {}) {
    try {
      await post('cart/add.js', { items: [{ id: variantId, quantity, properties }] });
      fly(from);
      await refresh();
      showError('');
      setTimeout(openCart, from && gsap && !reducedMotion() ? 450 : 0);
    } catch (err) {
      await refresh();
      openCart();
      showError(err.message);
    }
  }

  async function change(key, quantity) {
    cartRoot.classList.add('is-busy');
    try {
      cartState = await post('cart/change.js', { id: key, quantity });
      render();
      showError('');
    } catch (err) {
      showError(err.message);
    } finally {
      cartRoot.classList.remove('is-busy');
    }
  }

  window.DMCart = { add, open: openCart, close: closeCart, refresh };

  if (cartRoot) {
    // On the full cart page the icon simply leads there
    if (cartToggle && !document.body.classList.contains('template-cart')) {
      cartToggle.addEventListener('click', (e) => {
        e.preventDefault();
        (cartState ? Promise.resolve() : refresh()).then(openCart);
      });
    }
    $('.dm-cart__close', cartRoot).addEventListener('click', closeCart);
    $('.dm-cart__continue', cartRoot).addEventListener('click', closeCart);
    $('.dm-cart__scrim', cartRoot).addEventListener('click', closeCart);
    $('.dm-cart__list', cartRoot).addEventListener('click', (e) => {
      const line = e.target.closest('.dm-cart-line');
      if (!line) return;
      const item = cartState.items.find((i) => i.key === line.dataset.key);
      const step = e.target.closest('[data-step]');
      if (step) change(item.key, Math.max(0, item.quantity + +step.dataset.step));
      else if (e.target.closest('.dm-cart-line__remove')) change(item.key, 0);
    });

    // Same rule as the current shop: the terms must be accepted before checkout
    const termsBox = $('.dm-cart__terms-box', cartRoot);
    const termsError = $('.dm-cart__terms-error', cartRoot);
    $('.dm-cart__checkout', cartRoot).addEventListener('click', (e) => {
      if (termsBox.checked) return;
      e.preventDefault();
      termsError.hidden = false;
      termsBox.focus();
      if (gsap && !reducedMotion()) gsap.fromTo('.dm-cart__terms', { x: 0 }, { x: 6, duration: 0.07, repeat: 5, yoyo: true, ease: 'power1.inOut', clearProps: 'transform' });
    });
    termsBox.addEventListener('change', () => { if (termsBox.checked) termsError.hidden = true; });

    refresh();
  }

  // Dawn's "add to cart" button on pages not yet redesigned looks for <cart-notification>;
  // this stand-in hands the result to the side cart instead of Dawn's popup.
  if (!customElements.get('cart-notification')) {
    customElements.define('cart-notification', class extends HTMLElement {
      getSectionsToRender() { return [{ id: 'cart-icon-bubble' }]; }
      setActiveElement() {}
      renderContents() { refresh().then(openCart); }
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (cartRoot && !cartRoot.hidden) closeCart();
    else if (menu && !menu.hidden) closeMenu();
    else if (search && !search.hidden) { closeSearch(); searchToggle.focus(); }
  });

  /* ---------- Club signup: the page reloads after Shopify saves the email; say so clearly ---------- */

  const signup = document.getElementById('dm-footer-signup');
  if (signup) {
    const msg = signup.querySelector('.dm-signup__msg');
    const email = signup.querySelector('input[type="email"]');
    signup.addEventListener('submit', (e) => {
      if (email.checkValidity()) return;
      e.preventDefault();
      msg.textContent = 'צריך אימייל תקין';
      email.focus();
    });
    const posted = /[?&]customer_posted=true/.test(location.search);
    if (posted && !msg.textContent.trim()) msg.textContent = 'ברוכים הבאים למועדון! נעדכן אתכם ראשונים.';
    if (posted || msg.textContent.trim()) {
      // A pop-up bursts into the middle of the screen with a few sparks, then fades away by itself
      if (posted) {
        const pop = document.createElement('div');
        pop.className = 'dm-pop';
        pop.setAttribute('role', 'dialog');
        pop.setAttribute('aria-label', 'נרשמתם למועדון');
        pop.innerHTML = '<div class="dm-pop__card"><span class="dm-pop__star" aria-hidden="true">✦</span><p class="dm-pop__title">WELCOME TO THE CLUB</p><p class="dm-pop__text">נרשמתם! נעדכן אתכם ראשונים על הדרופים החדשים.</p><button type="button" class="dm-btn dm-btn--primary dm-pop__close">מעולה</button></div>';
        document.body.append(pop);
        const card = pop.querySelector('.dm-pop__card');
        let gone = false;
        const close = () => {
          if (gone) return;
          gone = true;
          if (!gsap || reducedMotion()) return pop.remove();
          gsap.to(card, { scale: 0.85, opacity: 0, duration: 0.3, ease: 'power2.in' });
          gsap.to(pop, { opacity: 0, duration: 0.35, onComplete: () => pop.remove() });
        };
        pop.addEventListener('click', (ev) => { if (ev.target === pop || ev.target.closest('.dm-pop__close')) close(); });
        document.addEventListener('keydown', (ev) => { if (ev.key === 'Escape') close(); }, { once: true });
        setTimeout(close, 4500);
        if (gsap && !reducedMotion()) {
          gsap.fromTo(pop, { opacity: 0 }, { opacity: 1, duration: 0.3 });
          gsap.fromTo(card, { scale: 0.4, opacity: 0, rotate: -6 }, { scale: 1, opacity: 1, rotate: 0, duration: 0.7, ease: 'back.out(1.8)' });
          gsap.fromTo('.dm-pop__star', { scale: 0, rotate: -180 }, { scale: 1, rotate: 0, duration: 0.8, delay: 0.2, ease: 'back.out(2.5)' });
          for (let i = 0; i < 12; i++) {
            const s = document.createElement('span');
            s.className = 'dm-pop__spark';
            s.textContent = '✦';
            s.setAttribute('aria-hidden', 'true');
            card.append(s);
            const angle = (Math.PI * 2 * i) / 12;
            const dist = 150 + Math.random() * 80;
            gsap.fromTo(s, { x: 0, y: 0, scale: 0.4, opacity: 1 }, { x: Math.cos(angle) * dist, y: Math.sin(angle) * dist, scale: 1.2, opacity: 0, rotate: 120, duration: 1.1, delay: 0.25, ease: 'power3.out', onComplete: () => s.remove() });
          }
        }
        pop.querySelector('.dm-pop__close').focus({ preventScroll: true });
      }
      // Then bring the signup into view, once pinned sections have taken their final height
      addEventListener('load', () => setTimeout(() => {
        if (lenis) lenis.scrollTo(signup, { offset: -innerHeight / 3, immediate: true });
        else signup.scrollIntoView({ block: 'center' });
        if (gsap && !reducedMotion()) gsap.fromTo(msg, { scale: 0.9, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(2)' });
      }, 400));
      // Leave a clean address, so a refresh doesn't show the note again
      history.replaceState(null, '', location.pathname + location.search.replace(/[?&]customer_posted=true/, '').replace(/^&/, '?'));
    }
  }

  // Pages with smooth scrolling hand their Lenis over, so menus and the cart can pause it
  window.DMShell = { setLenis: (l) => { lenis = l; }, openCart, closeCart };
})();
